import { z } from 'zod';

const stringOrNumber = z.union([z.string(), z.number()]);

export const ALLOWED_EVENT_TYPES = [
  'pong',
  'error',
  'lobby:server-info',
  'lobby:party-created',
  'lobby:party-updated',
  'lobby:party-deleted',
  'lobby:party-joined',
  'lobby:party-left',
  'lobby:party-dissolved',
  'lobby:party-chat',
  'lobby:party-stream-start',
  'lobby:party-stream-chunk',
  'lobby:party-stream-end',
  'lobby:party-typing',
  'lobby:party-chat-error',
  'lobby:tavian-response',
  'lobby:tavian-response-character',
  'lobby:party-tavian-response',
  'lobby:npc-stream-start',
  'lobby:npc-stream-chunk',
  'lobby:npc-stream-end',
  'lobby:char-create-response',
  'lobby:char-create-available',
  'lobby:level-up-response',
  'lobby:level-up-available',
  'lobby:redirect-to-npc',
  'lobby:character-released',
  'lobby:character-updated',
  'lobby:party-invite-received',
  'lobby:party-invite-removed',
  'lobby:dm-received',
  'lobby:dm-message',
  'lobby:dm-sent',
  'lobby:start-char-chat',
  'lobby:char-chat-started',
  'lobby:char-chat-message',
  'lobby:char-chat-closed',
  'lobby:char-chat-invite',
  'lobby:char-chat-participant-joined',
  'lobby:friend-added',
  'lobby:friend-removed',
  'lobby:friend-dm-started',
  'lobby:friend-dm-message',
  'lobby:friend-dm-closed',
  'lobby:friends-list-result',
  'lobby:party-decision-proposed',
  'lobby:party-decision-vote',
  'lobby:party-decision-resolved',
  'lobby:game-started',
  'lobby:quick-start-result',
  'lobby:start-game-result',
  'lobby:chat-message',
  'lobby:player-joined',
  'lobby:player-left',
  'lobby:player-ready',
  'lobby:cancelled',
  'lobby:updated',
  'lobby:session-state',
  'lobby:characters',
  'lobby:open-party-chat',
  'lobby:close-party-chat',
  // Chat room events (from chat-room-orchestrator)
  'chat:message',
  'chat:room-created',
  'chat:room-deleted',
  'chat:user-joined',
  'chat:user-left',
  // Background image generation events
  'lobby:background-generating',
  'lobby:background-generated',
  'lobby:background-error',
  // Portrait image generation events
  'lobby:portrait-generating',
  'lobby:portrait-generated',
  'lobby:portrait-error',
  // Decision events (from Rust backend)
  'lobby:decision-created',
  'lobby:vote-updated',
  'lobby:decision-resolved',
  // NPC chat events
  'lobby:party-chat-npc',
  // Tool result events
  'lobby:tavian-tool-result',
  // Level-up events
  'lobby:levelup-cancelled',
  // PM token balance push (on connect + after server-observed changes)
  'lobby:balance-update',
  // Server-driven audio (soundtrack engine — see lib/audio/)
  'lobby:soundtrack-play',
  'lobby:soundtrack-stop',
  'lobby:fx-play',
] as const;

const partySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    memberCharacterIds: z.array(z.string()),
  })
  .passthrough();

export const lobbyEventSchemas: Partial<
  Record<(typeof ALLOWED_EVENT_TYPES)[number], z.ZodTypeAny>
> = {
  'lobby:server-info': z
    .object({
      type: z.literal('lobby:server-info'),
      serverInstanceId: z.string(),
    })
    .passthrough(),
  'lobby:party-created': z
    .object({
      type: z.literal('lobby:party-created'),
      party: partySchema,
    })
    .passthrough(),
  'lobby:party-updated': z
    .object({
      type: z.literal('lobby:party-updated'),
      party: partySchema,
    })
    .passthrough(),
  'lobby:party-joined': z
    .object({
      type: z.literal('lobby:party-joined'),
      party: partySchema,
    })
    .passthrough(),
  'lobby:party-deleted': z
    .object({
      type: z.literal('lobby:party-deleted'),
      partyId: z.string(),
    })
    .passthrough(),
  'lobby:party-left': z
    .object({
      type: z.literal('lobby:party-left'),
      partyId: z.string(),
    })
    .passthrough(),
  'lobby:party-dissolved': z
    .object({
      type: z.literal('lobby:party-dissolved'),
      partyId: z.string(),
    })
    .passthrough(),
  'lobby:party-chat': z
    .object({
      type: z.literal('lobby:party-chat'),
      partyId: z.string(),
      characterId: z.string(),
      characterName: z.string(),
      message: z.string(),
      isNpc: z.boolean().optional(),
    })
    .passthrough(),
  'lobby:party-stream-start': z
    .object({
      type: z.literal('lobby:party-stream-start'),
      partyId: z.string(),
      streamId: z.string(),
      characterId: z.string(),
      characterName: z.string(),
      isNpc: z.boolean().optional(),
    })
    .passthrough(),
  'lobby:party-stream-chunk': z
    .object({
      type: z.literal('lobby:party-stream-chunk'),
      partyId: z.string(),
      streamId: z.string(),
      chunk: z.string(),
    })
    .passthrough(),
  'lobby:party-stream-end': z
    .object({
      type: z.literal('lobby:party-stream-end'),
      partyId: z.string(),
      streamId: z.string(),
      fullMessage: z.string().optional(),
    })
    .passthrough(),
  'lobby:party-typing': z
    .object({
      type: z.literal('lobby:party-typing'),
      partyId: z.string(),
      typing: z.boolean(),
    })
    .passthrough(),
  'lobby:party-chat-error': z
    .object({
      type: z.literal('lobby:party-chat-error'),
      partyId: z.string(),
      message: z.string(),
    })
    .passthrough(),
  'lobby:tavian-response': z
    .object({
      type: z.literal('lobby:tavian-response'),
      message: z.string().optional(),
      streamId: z.string().optional(),
    })
    .passthrough(),
  'lobby:tavian-response-character': z
    .object({
      type: z.literal('lobby:tavian-response-character'),
      charId: z.string(),
      session: z.unknown().optional(),
    })
    .passthrough(),
  'lobby:party-tavian-response': z
    .object({
      type: z.literal('lobby:party-tavian-response'),
      partyId: z.string(),
      message: z.string(),
      streamId: z.string().optional(),
    })
    .passthrough(),
  'lobby:npc-stream-start': z
    .object({
      type: z.literal('lobby:npc-stream-start'),
      streamId: z.string(),
      npc: z.string(), // Can be 'tavian', 'aldric', 'kira', or any NPC character ID
    })
    .passthrough(),
  'lobby:npc-stream-chunk': z
    .object({
      type: z.literal('lobby:npc-stream-chunk'),
      streamId: z.string(),
      chunk: z.string(),
    })
    .passthrough(),
  'lobby:npc-stream-end': z
    .object({
      type: z.literal('lobby:npc-stream-end'),
      streamId: z.string(),
      fullMessage: z.string(),
      charId: z.string().optional(),
      npc: z.string().optional(), // NPC ID for 1:1 chat responses
      isComplete: z.boolean().optional(),
      characterData: z
        .object({
          permanent: z.unknown(),
          session: z.unknown().optional(),
        })
        .optional(),
    })
    .passthrough(),
  'lobby:char-create-response': z
    .object({
      type: z.literal('lobby:char-create-response'),
      streamId: z.string().optional(),
      charId: z.string().optional(),
      response: z.string().optional(),
      isComplete: z.boolean().optional(),
      characterData: z
        .object({
          permanent: z.unknown(),
          session: z.unknown().optional(),
        })
        .optional(),
    })
    .passthrough(),
  'lobby:char-create-available': z
    .object({
      type: z.literal('lobby:char-create-available'),
      available: z.boolean(),
    })
    .passthrough(),
  'lobby:level-up-response': z
    .object({
      type: z.literal('lobby:level-up-response'),
      charId: z.string(),
      isComplete: z.boolean(),
    })
    .passthrough(),
  'lobby:level-up-available': z
    .object({
      type: z.literal('lobby:level-up-available'),
      available: z.boolean(),
    })
    .passthrough(),
  'lobby:redirect-to-npc': z
    .object({
      type: z.literal('lobby:redirect-to-npc'),
      targetNpc: z.string(), // Can be 'tavian', 'aldric', 'kira', or any NPC character ID
      reason: z.string().optional(),
    })
    .passthrough(),
  'lobby:character-released': z
    .object({
      type: z.literal('lobby:character-released'),
      characterId: z.string(),
      characterName: z.string().optional(),
    })
    .passthrough(),
  'lobby:character-updated': z
    .object({
      type: z.literal('lobby:character-updated'),
      characterId: z.string(),
      updates: z.unknown().optional(),
    })
    .passthrough(),
  'lobby:party-invite-received': z
    .object({
      type: z.literal('lobby:party-invite-received'),
      invite: z.unknown(),
    })
    .passthrough(),
  'lobby:party-invite-removed': z
    .object({
      type: z.literal('lobby:party-invite-removed'),
      inviteId: z.string(),
    })
    .passthrough(),
  'lobby:dm-received': z
    .object({
      type: z.literal('lobby:dm-received'),
      fromCharacterId: z.string(),
      fromCharacterName: z.string(),
      message: z.string(),
      avatar: z.string().optional(),
    })
    .passthrough(),
  // DM message from Rust backend (has isNpc field)
  'lobby:dm-message': z
    .object({
      type: z.literal('lobby:dm-message'),
      fromCharacterId: z.string(),
      fromCharacterName: z.string(),
      message: z.string(),
      isNpc: z.boolean(),
    })
    .passthrough(),
  'lobby:dm-sent': z
    .object({
      type: z.literal('lobby:dm-sent'),
      toCharacterId: z.string(),
      toCharacterName: z.string(),
      message: z.string(),
      avatar: z.string().optional(),
    })
    .passthrough(),
  // Tavian requests opening a 1:1 chat panel with a character
  'lobby:start-char-chat': z
    .object({
      type: z.literal('lobby:start-char-chat'),
      characterId: z.string(),
      characterName: z.string(),
    })
    .passthrough(),
  'lobby:char-chat-started': z
    .object({
      type: z.literal('lobby:char-chat-started'),
      chatSessionId: z.string(),
      // Rust backend sends participants array
      participants: z
        .array(
          z.object({
            charId: z.string(),
            charName: z.string(),
            isNpc: z.boolean(),
            userId: z.string().optional(),
          })
        )
        .optional(),
      // Legacy fields for TypeScript-initiated chats
      initiatorCharacterId: z.string().optional(),
      initiatorCharacterName: z.string().optional(),
      targetCharacterId: z.string().optional(),
      targetCharacterName: z.string().optional(),
      targetType: z.enum(['pc', 'npc']).optional(),
    })
    .passthrough(),
  'lobby:char-chat-message': z
    .object({
      type: z.literal('lobby:char-chat-message'),
      chatSessionId: z.string(),
      fromCharacterId: z.string(),
      fromCharacterName: z.string(),
      message: z.string(),
      isNpc: z.boolean().optional(),
      isStreaming: z.boolean().optional(),
      avatar: z.string().optional(),
    })
    .passthrough(),
  // New participant joined a character chat session (N:N chat support)
  'lobby:char-chat-participant-joined': z
    .object({
      type: z.literal('lobby:char-chat-participant-joined'),
      chatSessionId: z.string(),
      participant: z.object({
        charId: z.string(),
        charName: z.string(),
        isNpc: z.boolean(),
        userId: z.string().optional(),
      }),
    })
    .passthrough(),
  // Friends system
  'lobby:friend-added': z
    .object({
      type: z.literal('lobby:friend-added'),
      friendUserId: z.string(),
      friendDisplayName: z.string(),
      alreadyFriends: z.boolean(),
    })
    .passthrough(),
  'lobby:friend-dm-started': z
    .object({
      type: z.literal('lobby:friend-dm-started'),
      dmSessionId: z.string(),
      partnerUserId: z.string(),
      partnerDisplayName: z.string(),
    })
    .passthrough(),
  'lobby:friend-dm-message': z
    .object({
      type: z.literal('lobby:friend-dm-message'),
      dmSessionId: z.string(),
      fromUserId: z.string(),
      fromDisplayName: z.string(),
      message: z.string(),
    })
    .passthrough(),
  'lobby:friend-dm-closed': z
    .object({
      type: z.literal('lobby:friend-dm-closed'),
      dmSessionId: z.string(),
      partnerUserId: z.string(),
    })
    .passthrough(),
  'lobby:friend-removed': z
    .object({
      type: z.literal('lobby:friend-removed'),
      friendUserId: z.string(),
    })
    .passthrough(),
  'lobby:friends-list-result': z
    .object({
      type: z.literal('lobby:friends-list-result'),
      friends: z.array(
        z.object({
          userId: z.string(),
          displayName: z.string(),
          online: z.boolean(),
          inSession: z.boolean(),
          avatarUrl: z.string().optional(),
          avatarEmoji: z.string().optional(),
        })
      ),
    })
    .passthrough(),
  'lobby:party-decision-proposed': z
    .object({
      type: z.literal('lobby:party-decision-proposed'),
      partyId: z.string(),
      decision: z
        .object({
          id: z.string(),
          partyId: z.string(),
          description: z.string(),
          expiresAt: stringOrNumber.optional(),
          type: z.string().optional(),
          status: z.string().optional(),
          votes: z.array(z.unknown()).optional(),
        })
        .passthrough(),
    })
    .passthrough(),
  'lobby:game-started': z
    .object({
      type: z.literal('lobby:game-started'),
      lobbyId: z.string().optional(),
      gameSessionId: z.string(),
      gameServerWsUrl: z.string().optional(),
      partyId: z.string().optional(),
      moduleId: z.string().optional(),
      moduleName: z.string().optional(),
    })
    .passthrough(),
  'lobby:quick-start-result': z
    .object({
      type: z.literal('lobby:quick-start-result'),
      status: z.enum(['started', 'resumed', 'failed']),
      code: z.string().optional(),
      message: z.string().optional(),
      gameSessionId: z.string().optional(),
    })
    .passthrough(),
  'lobby:start-game-result': z
    .object({
      type: z.literal('lobby:start-game-result'),
      code: z.string(),
      message: z.string(),
    })
    .passthrough(),
  'lobby:chat-message': z
    .object({
      type: z.literal('lobby:chat-message'),
      message: z
        .object({
          id: z.string().optional(),
          lobbyId: z.string().optional(),
          userId: z.string(),
          displayName: z.string(),
          message: z.string(),
          timestamp: stringOrNumber,
          type: z.string().optional(),
        })
        .passthrough(),
    })
    .passthrough(),
  'lobby:player-joined': z
    .object({
      type: z.literal('lobby:player-joined'),
      lobbyId: z.string(),
      player: z.unknown(),
    })
    .passthrough(),
  'lobby:player-left': z
    .object({
      type: z.literal('lobby:player-left'),
      lobbyId: z.string(),
      userId: z.string(),
    })
    .passthrough(),
  'lobby:cancelled': z
    .object({
      type: z.literal('lobby:cancelled'),
      lobbyId: z.string(),
      reason: z.string().optional(),
    })
    .passthrough(),
  'lobby:updated': z
    .object({
      type: z.literal('lobby:updated'),
      lobby: z
        .object({
          id: z.string(),
          parties: z.array(partySchema).optional(),
        })
        .passthrough(),
    })
    .passthrough(),
  'lobby:session-state': z
    .object({
      type: z.literal('lobby:session-state'),
      userId: z.string().optional(),
      activeNpc: z.string(), // Can be 'tavian', 'aldric', 'kira'
      activeNpcSession: z.string().optional(),
      charCreationInProgress: z.boolean().optional(),
      levelUpInProgress: z.boolean().optional(),
      charId: z.string().optional(),
      characterData: z
        .object({
          permanent: z.unknown(),
          session: z.unknown().optional(),
        })
        .optional(),
      partyId: z.string().optional(),
      partyName: z.string().optional(),
      activeGameSession: z.string().optional(),
      characterCleared: z.boolean().optional(),
    })
    .passthrough(),
  'lobby:characters': z
    .object({
      type: z.literal('lobby:characters'),
      characters: z.array(z.unknown()),
    })
    .passthrough(),
  'lobby:open-party-chat': z
    .object({
      type: z.literal('lobby:open-party-chat'),
      partyId: z.string(),
      partyName: z.string().optional(),
    })
    .passthrough(),
  'lobby:close-party-chat': z
    .object({
      type: z.literal('lobby:close-party-chat'),
      partyId: z.string(),
    })
    .passthrough(),
  'lobby:char-chat-closed': z
    .object({
      type: z.literal('lobby:char-chat-closed'),
      chatSessionId: z.string(),
      closedByCharacterId: z.string().optional(),
    })
    .passthrough(),
  'lobby:char-chat-invite': z
    .object({
      type: z.literal('lobby:char-chat-invite'),
      // TypeScript fields
      chatSessionId: z.string().optional(),
      inviterCharacterId: z.string().optional(),
      inviterCharacterName: z.string().optional(),
      // Rust backend fields
      chatId: z.string().optional(),
      characterId: z.string().optional(),
      characterName: z.string().optional(),
    })
    .passthrough(),
  'lobby:party-decision-vote': z
    .object({
      type: z.literal('lobby:party-decision-vote'),
      partyId: z.string(),
      decisionId: z.string(),
      vote: z
        .object({
          characterId: z.string(),
          vote: z.enum(['yes', 'no']),
          timestamp: stringOrNumber.optional(),
        })
        .optional(),
    })
    .passthrough(),
  'lobby:party-decision-resolved': z
    .object({
      type: z.literal('lobby:party-decision-resolved'),
      partyId: z.string(),
      decision: z
        .object({
          id: z.string(),
          partyId: z.string(),
          description: z.string(),
          expiresAt: stringOrNumber.optional(),
          type: z.string().optional(),
          status: z.string().optional(),
          votes: z.array(z.unknown()).optional(),
          resolvedAt: stringOrNumber.optional(),
        })
        .passthrough(),
    })
    .passthrough(),
  'lobby:player-ready': z
    .object({
      type: z.literal('lobby:player-ready'),
      // Rust backend fields
      lobbyId: z.string().optional(),
      userId: z.string().optional(),
      readyState: z.string().optional(),
      // Legacy fields
      playerId: z.string().optional(),
      ready: z.boolean().optional(),
    })
    .passthrough(),
  error: z
    .object({
      type: z.literal('error'),
      message: z.string(),
      code: z.string().optional(),
    })
    .passthrough(),
  'chat:message': z
    .object({
      type: z.literal('chat:message'),
      roomId: z.string(),
      senderId: z.string(),
      message: z.string(),
    })
    .passthrough(),
  'chat:room-created': z
    .object({
      type: z.literal('chat:room-created'),
      roomId: z.string(),
      name: z.string().optional(),
    })
    .passthrough(),
  'chat:room-deleted': z
    .object({
      type: z.literal('chat:room-deleted'),
      roomId: z.string(),
    })
    .passthrough(),
  'chat:user-joined': z
    .object({
      type: z.literal('chat:user-joined'),
      roomId: z.string(),
      userId: z.string(),
    })
    .passthrough(),
  'chat:user-left': z
    .object({
      type: z.literal('chat:user-left'),
      roomId: z.string(),
      userId: z.string(),
    })
    .passthrough(),
  // Background image generation events
  'lobby:background-generating': z
    .object({
      type: z.literal('lobby:background-generating'),
      userId: z.string(),
      roomContext: z
        .object({
          roomType: z.enum(['tavian', 'aldric', 'kira', 'grolt', 'party', 'private', 'lobby']),
          partyId: z.string().optional(),
          targetCharacterId: z.string().optional(),
        })
        .passthrough(),
    })
    .passthrough(),
  'lobby:background-generated': z
    .object({
      type: z.literal('lobby:background-generated'),
      userId: z.string(),
      roomContext: z
        .object({
          roomType: z.enum(['tavian', 'aldric', 'kira', 'grolt', 'party', 'private', 'lobby']),
          partyId: z.string().optional(),
          targetCharacterId: z.string().optional(),
        })
        .passthrough(),
      mediaUrl: z.string(),
      mediaType: z.enum(['video', 'image']),
    })
    .passthrough(),
  'lobby:background-error': z
    .object({
      type: z.literal('lobby:background-error'),
      userId: z.string(),
      roomContext: z
        .object({
          roomType: z.enum(['tavian', 'aldric', 'kira', 'grolt', 'party', 'private', 'lobby']),
          partyId: z.string().optional(),
          targetCharacterId: z.string().optional(),
        })
        .passthrough(),
      error: z.string(),
    })
    .passthrough(),
  // Portrait image generation events
  'lobby:portrait-generating': z
    .object({
      type: z.literal('lobby:portrait-generating'),
      userId: z.string(),
      characterId: z.string(),
    })
    .passthrough(),
  'lobby:portrait-generated': z
    .object({
      type: z.literal('lobby:portrait-generated'),
      userId: z.string(),
      characterId: z.string(),
      imageUrl: z.string(),
      avatarUrl: z.string().optional(),
    })
    .passthrough(),
  'lobby:portrait-error': z
    .object({
      type: z.literal('lobby:portrait-error'),
      userId: z.string(),
      characterId: z.string(),
      error: z.string(),
    })
    .passthrough(),
  // Decision events (from Rust backend)
  'lobby:decision-created': z
    .object({
      type: z.literal('lobby:decision-created'),
      partyId: z.string(),
      decisionId: z.string(),
      decisionType: z.string(),
      votesRequired: z.number(),
      votesYes: z.number(),
      votesNo: z.number(),
    })
    .passthrough(),
  'lobby:vote-updated': z
    .object({
      type: z.literal('lobby:vote-updated'),
      partyId: z.string(),
      decisionId: z.string(),
      votesYes: z.number(),
      votesNo: z.number(),
      votedCharIds: z.array(z.string()),
    })
    .passthrough(),
  'lobby:decision-resolved': z
    .object({
      type: z.literal('lobby:decision-resolved'),
      partyId: z.string(),
      decisionId: z.string(),
      decisionType: z.string(),
      status: z.string(),
    })
    .passthrough(),
  // NPC chat in party
  'lobby:party-chat-npc': z
    .object({
      type: z.literal('lobby:party-chat-npc'),
      partyId: z.string(),
      npcId: z.string(),
      npcName: z.string(),
      message: z.string(),
      isNpc: z.boolean().optional(),
    })
    .passthrough(),
  // Tavian tool results
  'lobby:tavian-tool-result': z
    .object({
      type: z.literal('lobby:tavian-tool-result'),
      toolName: z.string(),
      result: z.unknown(),
    })
    .passthrough(),
  // Level-up cancelled
  'lobby:levelup-cancelled': z
    .object({
      type: z.literal('lobby:levelup-cancelled'),
      charId: z.string(),
    })
    .passthrough(),
};

export const lobbyEventSchema = z
  .object({
    type: z.enum(ALLOWED_EVENT_TYPES),
  })
  .passthrough();

export const websocketSchemas = {
  stringOrNumber,
  partySchema,
  lobbyEventSchema,
  lobbyEventSchemas,
};
